// The one thing every list in the panel shows when it has no rows to show.
//
// Before this, an empty table said its piece in a single grey sentence — true,
// but it read as a dead end. An empty list is almost never a dead end: it is a
// list that has not been filled yet, a search that was too narrow, or a request
// that never came back, and those are three different situations that want
// three different answers. So the shape here is always the same three parts —
// a headline, a line of explanation, and, when there is something to do about
// it, the thing to do:
//
//   No old clients yet   the headline: what is true, in the list's own words
//   Clients you...       why it is true and what fills it, at most two lines
//   [ Go to active ]     the one action worth offering, when there is one
//
// The description is the part that earns its keep. "No old clients yet" leaves
// the reader wondering whether something went wrong; the line under it says
// nothing is wrong and where their clients actually are. Where there is nothing
// useful to add, leave it off rather than padding it out.
//
// Most of those states get the compact layout above and no picture. A tinted
// disc holding a line icon was tried on all of them and taken out: for a search
// that matched nothing or a fetch that failed, the icon is a picture of the
// noun the list is empty of — an inbox, a receipt — which tells the reader what
// screen they are on, and they already know that.
//
// The one case that earns more is a list that has never had a row in it. That
// is not a list reporting a result; it is the first thing a new workspace sees
// on the screen, and it has a job the compact layout cannot do — say what the
// screen is for before asking anyone to fill it. `variant="welcome"` is that
// layout: the illustration, a headline at heading size, and a short list of
// what the feature buys, set left so the eye reads down one edge.
//
//   [icon]
//   Add your first client        the headline, at heading size
//   Create your client list...   one line on what the screen is
//
//   Your clients, organised      what the reader gets, named
//   | • Keep every client...     three at most, one line each
//   | • Create & share...
//
//   [ + Add client ]             the same one action, at full size
//
// Use it only where the list is empty because nothing has been created yet.
// A failed load or a narrow search still gets the compact layout — a welcome
// mat under an error reads as though the error were the intended screen.
//
// The highlights are what set the block left. A bulleted list wants a left
// edge — the rule down its side has nothing to hang off a ragged centre — so
// a welcome state that has them is set left and inset from the card's edge.
// Without them there is nothing to align, and the block centres in its card
// like the compact state does; the dashboard's recent-clients table is that
// case, where the same mark and headline appear over a card too short to
// carry the list as well. Deriving it rather than taking an `align` prop is
// what keeps the two bad pairings — centred bullets, and a left-set block
// with nothing to justify the inset — from being reachable at all.

import React from "react";

/** The "what you get" block under a welcome state's description. */
export interface EmptyStateHighlights {
  /** Names what the list below is — "Your clients, organised". */
  title: string;
  /** One line each, three at most. Nodes, so a phrase can carry weight. */
  items: React.ReactNode[];
}

export interface EmptyStateProps {
  /** What is true, said plainly. One short line — this is the headline. */
  title: string;
  /** Why it is true, and what would fill the list. Two lines at the very most. */
  description?: React.ReactNode;
  /** The one thing worth offering — a retry, or the way to the list that has rows. */
  action?: React.ReactNode;
  /**
   * `compact` (the default) is the three-part state every list falls back to.
   * `welcome` is the first-run layout, for a list nothing has been added to
   * yet — see the note above for when each one is right.
   */
  variant?: "compact" | "welcome";
  /** Welcome only: the tinted mark above the headline. Ignored when compact. */
  illustration?: React.ReactNode;
  /**
   * Welcome only: how big the words are. `md` (the default) is the page-sized
   * type a first-run screen wears when the empty list *is* the screen — an
   * empty clients table, where there is nothing else to read.
   *
   * `sm` is the same layout set to the type scale of the surrounding text, for
   * a welcome state that sits inside a screen with other things on it. There
   * the page-sized headline is louder than the real heading above it, and an
   * empty list shouting over the form that fills it reads as the point of the
   * screen rather than as a footnote to it.
   */
  size?: "md" | "sm";
  /** Welcome only: what the feature buys, named and listed. Ignored when compact. */
  highlights?: EmptyStateHighlights;
  /** Layout only (margins). Nothing here has a second visual style to pick. */
  className?: string;
}

const WelcomeEmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  action,
  illustration,
  highlights,
  size = "md",
  className = "",
}) => (
  // Left or centred on whether there is a list to align — see the note above.
  // Capped either way, so the description stays paragraph-width at any card
  // size rather than running across the whole table.
  <div
    className={`flex  flex-col px-6 py-10 ${
      highlights
        ? "items-start text-left sm:px-10 lg:px-24"
        : "mx-auto items-center text-center"
    } ${className}`}
  >
    {illustration && <div className="mb-4 flex-shrink-0">{illustration}</div>}

    {/* Sized and weighted as a heading but not marked up as one: this sits
        inside a card that already has a real heading above it, and a second
        one in the document outline would claim a section that does not exist.
        Only a step above the card's own heading, not a banner — the block is
        an invitation inside the screen, not the screen's title. */}
    <p
      className={`font-semibold leading-snug text-[#00424B] ${
        size === "sm"
          ? "text-[16px] 3xl:text-[17px]"
          : "text-[22px] 3xl:text-[24px]"
      }`}
    >
      {title}
    </p>

    {description && (
      <p
        className={`mt-1 leading-[1.6] text-ink-muted ${
          size === "sm"
            ? "text-[13px] 3xl:text-[14px]"
            : "text-[16px] 3xl:text-[18px]"
        }`}
      >
        {description}
      </p>
    )}

    {highlights && (
      <div className="mt-4">
        <p className="text-[14px] font-medium text-ink">{highlights.title}</p>

        {/* The rule down the left is what makes these read as one group
            hanging off the line above rather than as three loose sentences —
            which is also why the lines are set close: spread out, each one
            reads as its own statement and the rule has nothing to bind. */}
        <ul className="mt-1.5 space-y-1 border-l-[6px] border-[#E7E8EC] pl-4">
          {highlights.items.map((item, index) => (
            <li
              key={index}
              className="flex gap-2 text-[14px] leading-[1.6] text-ink-muted"
            >
              {/* Drawn rather than listed as a marker so the bullet keeps its
                  own colour and does not wander with the text's indent. */}
              <span aria-hidden className="text-ink-subtle">
                •
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const EmptyState: React.FC<EmptyStateProps> = (props) => {
  const { title, description, action, variant = "compact", className = "" } = props;

  if (variant === "welcome") return <WelcomeEmptyState {...props} />;

  return (
    // Capped and centred rather than filling its container: at a table's full
    // width the description would run as one long line across the card and stop
    // reading as a paragraph under the headline.
    <div
      className={`mx-auto flex max-w-[400px] flex-col items-center px-6 py-12 text-center ${className}`}
    >
      {/* Sized and weighted as a heading but not marked up as one — see above. */}
      <p className="text-[15px] font-semibold text-ink">{title}</p>

      {description && (
        <p className="mt-1.5 text-[13px] leading-[1.6] text-ink-muted">
          {description}
        </p>
      )}

      {action && <div className="mt-5">{action}</div>}
    </div>
  );
};

/**
 * The empty state a search that matched nothing gets. Said the same way
 * everywhere, because it means the same thing everywhere: the list is fine,
 * the query was just too narrow.
 *
 * `plural` is the lowercase plural the list calls its rows — "clients",
 * "enquiries", "services" — and lands in the headline as written.
 */
export const searchEmptyState = (plural: string): EmptyStateProps => ({
  title: `No ${plural} match your search`,
  description:
    "Check the spelling, or clear the search to see the whole list again.",
});

/**
 * The empty state a load that never came back gets. The headline is the list's
 * own — "We could not load your clients" — because which list failed is the
 * part the reader needs; the rest is the same reassurance every time.
 *
 * Note it says nothing has been lost. An empty table under a failed fetch is
 * the same picture as a table whose rows have been deleted, and that is the
 * fear worth answering before offering the retry.
 */
export const failedEmptyState = (
  title: string,
  action?: React.ReactNode
): EmptyStateProps => ({
  title,
  description:
    "Nothing has been lost - this is a problem reaching the server. Try again in a moment.",
  action,
});

/**
 * The empty state a detail screen gets when the record behind its URL is not
 * there — a deleted client, a plan someone kept a link to.
 *
 * What the reader needs is the way back to the list, which is what `action`
 * should be.
 */
export const notFoundEmptyState = (
  title: string,
  description: React.ReactNode,
  action?: React.ReactNode
): EmptyStateProps => ({
  title,
  description,
  action,
});

export default EmptyState;
